import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import dayjs from 'dayjs';
import QueryString from 'qs';
import { getCookie } from 'utils/bom-dom-manipulation/cookies';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import SessionStorage from 'utils/bom-dom-manipulation/session-storage';
import { RegFieldsDefaultsJson } from 'utils/constants/account/reg-fields-json';
import {
  checkUserStatus,
  userActiveStep
} from 'utils/constants/account/user-status';
import { HOUR_PER_DAY } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { UtilsExternal } from 'utils/external';
import { GLOBALS } from 'utils/generic/global-variables';
import { lazy } from 'utils/generic/lazy';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { showToastSuccess } from 'utils/swarm/swarm-success-handler';
import {
  EAccountWidgetLayoutType,
  UserOptInsResponse
} from 'interfaces/account-settings';
import { EFavoriteTabsTypes } from 'interfaces/generic';
import { PartnerConfig } from 'interfaces/partner-config';
import {
  getAccountJson,
  getPlayerOptIns,
  setSelfExclusion
} from 'services/account-settings';
import { AfterSignIn } from 'services/afterSignIn';
import { removeUserFromStorage } from 'services/authentication';
import { GpsTracking } from './_partials/components/GpsTracking';
import { AuthSectionSkeleton } from './_partials/Skeleton/AuthSectionSkeleton';
import { useCheckDepositReturnUrl } from './hooks/useCheckDepositReturnUrl';
import { useFetchSectionsData } from './hooks/useFetchSectionsData';
import { useSeonIntegration } from './hooks/useSeonIntegration';
import { Accounts as Pages } from './views';
import { useHandlePreferredLanguage } from 'elements/Accounts/hooks/useHandlePreferredLanguage';
import { Loading } from 'components/Loading';
import { Popup } from 'components/Popup';
import {
  addQueryInString,
  getSavedLocation,
  QueryRoute,
  updateQuery
} from 'components/QueryRoute';
import { Portal } from 'components/Shared/Portal';
import { ChangeThemeSkeleton } from 'newcomponents/Shared/account/ChangeTheme/ChangeThemeSkeleton';
import { useAccountLogout } from 'hooks/auth/useAccountLogout';
import { useLoginLimit } from 'hooks/auth/useLoginLimit';
import { useRestoreLogin } from 'hooks/auth/useRestoreLogin';
import { useUserNotifications } from 'hooks/auth/useUserNotifications';
import { useSearchParams } from 'hooks/useSearchParams';
import { useFetchAccountParams } from 'newhooks/account/useFetchAccountParams';
import { resetPayments, setClientGamingStateInfo } from 'store/actions';
import { setRegField } from 'store/actions/app-data';
import {
  removeUserData,
  setPaymentDepositPromo,
  setShowAutoidentVerificationModal,
  setUserOptIns
} from 'store/actions/user-data';
import {
  getFavoriteOnHeaderOpen,
  getPersonalizedGenericSettings,
  getRegFieldData,
  getRemainingSessionDuration,
  getSectionsData,
  getShowAdditionalInfo
} from 'store/selectors/app-data';
import {
  getIsConnected,
  getPartnerConfigs,
  getReConnected
} from 'store/selectors/socket';
import {
  getCashbackNotifications,
  getIsLoggedIn,
  getLoyaltyNotifications,
  getPendingSuperBets,
  getRegIsFinished,
  getShouldGetUserOptIns,
  getUser,
  getUserOptIns
} from 'store/selectors/user-data';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const { LoyaltyCurrentLevelBadge } = lazy(
  () =>
    import(
      /* webpackChunkName: "LoyaltyCurrentLevelBadge" */ 'newcomponents/Shared/account/LoyaltyCurrentLevelBadge'
    )
);

const { FavoriteOnHeaderPopup } = lazy(
  () =>
    import(
      /* webpackChunkName: "favorite-on-header-popup" */ './_partials/Favorites/FavoriteOnHeaderPopup'
    )
);

const { BankIDIntegration } = lazy(
  () =>
    import(
      /* webpackChunkName: "bank-id-integration" */ './views/Verification/BankIDIntegration'
    )
);

const { ChangeTheme } = lazy(
  () =>
    import(
      /* webpackChunkName: "change-theme-switcher" */ 'newcomponents/Shared/account/ChangeTheme'
    )
);

const { ProfileInfo } = lazy(
  () =>
    import(/* webpackChunkName: "accounts-pages" */ 'components/ProfileInfo')
);

const { CryptoWallets } = lazy(
  () =>
    import(
      /* webpackChunkName: "crypto-wallets" */ 'newcomponents/Shared/account/CryptoWallets'
    )
);

const { Login } = lazy(
  () => import(/* webpackChunkName: "accounts-login" */ 'components/Login')
);

const { RegisterOnPage } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-register-on-page" */ './views/RegisterOnPage'
    )
);

const { VerifyEmailModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-verify-email-modal" */ './views/Verification/VerifyEmailModal'
    )
);

const { CheckEmailModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-check-email-modal" */ './views/Verification/CheckEmailModal'
    )
);

const { DocumentUploadModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-tru-narrative-document-upload-modal" */ 'components/DocumentUploadModal'
    )
);

const { CounterOfferModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-counter-offer-modal" */ 'components/CounterOfferModal'
    )
);

const { LoginFreeze } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-login-freeze" */ 'newcomponents/Shared/LoginFreeze'
    )
);

const { RemainingSessionDurationModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-remaining-session-duration-modal" */ 'newcomponents/Shared/account/RemainingSessionDurationModal'
    )
);

const { PlayerInfo } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-player-info-modal" */ 'components/PlayerInfo'
    )
);

const { AdditionalInfoPopup } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-additional-info-popup" */ 'newcomponents/Shared/account/AdditionalInfoPopup'
    )
);

const { AuthSection } = lazy(
  () =>
    import(
      /* webpackChunkName: "accounts-auth-section" */ './_partials/AuthSection'
    )
);

const { AutoidentVerificationModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "account-autoident-verification-modal" */ 'components/AutoidentVerificationModal'
    )
);

const { TermsAndConditions } = lazy(
  () =>
    import(
      /* webpackChunkName: "terms-and-conditions" */ './_partials/TermsAndConditions'
    )
);

const { NotVerifiedMessage } = lazy(
  () =>
    import(
      /* webpackChunkName: "not-verified-message" */ './_partials/NotVerifiedMessage'
    )
);

const { VerifyAccountModalRedirect } = lazy(
  () =>
    import(
      /* webpackChunkName: "verify-account-modal" */ './_partials/components/VerifyAccountModalRedirect'
    )
);

const { AccountGenericModals } = lazy(
  () =>
    import(
      /* webpackChunkName: "account-generic-modals" */ 'elements/Accounts/modals/AccountGenericModals'
    )
);

const { NotificationCards } = lazy(
  () =>
    import(
      /* webpackChunkName: "notification-cashback-cards" */ 'newcomponents/UI/Notification/NotificationCards'
    )
);

const { PlayerActivityChecker } = lazy(
  () =>
    import(
      /* webpackChunkName: "player-activity-checker" */ 'newcomponents/Shared/account/PlayerActivityChecker'
    )
);

export type TAccountConfigs = {
  accountLayout: EAccountWidgetLayoutType;
  slideView?: 'signin' | 'signup';
  horizontalView?: boolean;
  accountSignInButtonType?: string;
  accountSignInButtonStyle?: string;
  accountSignInButtonIcon?: string;
  accountSignUpButtonType?: string;
  accountSignUpButtonStyle?: string;
  accountSignUpButtonIcon?: string;
  accountPrevButtonStyle?: string;
  accountNextButtonStyle?: string;
  accountButtonsDistance?: string | number;
  mounted: boolean;
  signInBtn?: boolean;
  signUpBtn?: boolean;
  favorite?: string;
  favoriteTabs?: EFavoriteTabsTypes[];
  fromIframe?: boolean;
  signInPopupAlias?: string;
  signUpPopupAlias?: string;
  inPopup?: boolean;
};

type Props = {
  configs: TAccountConfigs;
};

export const Accounts = ({ configs }: Props): ReactElement | null => {
  const {
    accountLayout: _layout,
    slideView,
    horizontalView,
    accountSignInButtonStyle,
    accountSignUpButtonStyle,
    accountButtonsDistance,
    mounted,
    accountPrevButtonStyle,
    accountNextButtonStyle,
    signInBtn,
    signUpBtn,
    favorite,
    favoriteTabs: _favoriteTabs,
    fromIframe,
    signInPopupAlias,
    signUpPopupAlias,
    inPopup
  } = configs;

  useLoginLimit();
  useRestoreLogin();
  useUserNotifications();
  useFetchSectionsData();
  useSeonIntegration();
  useCheckDepositReturnUrl();
  useHandlePreferredLanguage();

  const { accountParams } = useFetchAccountParams();

  const layout = _layout || 'popup';

  const favoriteTabs: EFavoriteTabsTypes[] | undefined = _favoriteTabs?.map(
    item => String(item) as EFavoriteTabsTypes
  );

  const [showBankModal, setShowBankModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [routeHistory, setRouteHistory] = useState<string[]>([]);
  const [onPageCurrentSlide, setOnPageCurrentSlide] = useState<
    'signin' | 'signup' | undefined
  >(layout === EAccountWidgetLayoutType.onPage ? slideView : undefined);

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const queries = useSearchParams();
  const accountLogout = useAccountLogout();
  const restoreLogin = useRestoreLogin(true);

  const isLoggedIn = useSelector(getIsLoggedIn);
  const isRegFinished = useSelector(getRegIsFinished);
  const user = useSelector(getUser);
  const showAdditionalInfo = useSelector(getShowAdditionalInfo);
  const pendingSuperBets = useSelector(getPendingSuperBets);
  const regFieldsData = useSelector(getRegFieldData);
  const userOptIns = useSelector(getUserOptIns);
  const shouldGetOptIns = useSelector(getShouldGetUserOptIns);
  const remainingSessionDuration = useSelector(getRemainingSessionDuration);
  const partnerConfigs = useSelector(getPartnerConfigs) as PartnerConfig;
  const accountSections = useSelector(getSectionsData);
  const cashbackNotifications = useSelector(getCashbackNotifications);
  const loyaltyNotifications = useSelector(getLoyaltyNotifications);
  const { accountSettings } = useSelector(getPersonalizedGenericSettings);
  const reConnected = useSelector(getReConnected);
  const isConnected = useSelector(getIsConnected);
  const favoritePopupOpen = useSelector(getFavoriteOnHeaderOpen);

  const isLoyaltyV2 =
    partnerConfigs?.is_using_loyalty_program && partnerConfigs.is_loyalty_v2;

  const additionalInfoPopupInLS = JSON.parse(
    LocalStorage.getItem(storageKeyName('account', 'ADDITIONAL_INFO_POPUP'))
  );

  const auth_data_x =
    JSON.parse(LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))) &&
    !SpringConfigs.MOCKED_DATA;

  const customPopupAlias = LocalStorage.getItem(
    storageKeyName('account', 'POPUP_OPEN')
  );

  const showAdditionalInfoLS = JSON.parse(
    LocalStorage.getItem(storageKeyName('customPopup', 'SHOW_ADDITIONAL_INFO'))
  );

  const customPopupOpen =
    customPopupAlias !== 'null' && customPopupAlias !== 'undefined';

  window.hasPopup = signInPopupAlias || signUpPopupAlias;

  const isInPopup = inPopup === undefined ? false : inPopup;

  const openCustomPopup =
    Object.keys(queries).length > 1 &&
    queries.accounts &&
    ((queries.login && signInPopupAlias) ||
      (queries.register && signUpPopupAlias)) &&
    !queries.mode &&
    !queries.multiview &&
    !queries.preview;

  useEffect(() => {
    const authData = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))
    );

    if (openCustomPopup && !authData && !isInPopup) {
      const casinoGameUrl = JSON.parse(
        LocalStorage.getItem(storageKeyName('casino', 'SELECTED_GAME_URL'))
      );

      const prevRoutes =
        JSON.parse(
          SessionStorage.getItem(storageKeyName('app', 'PREVIOUS_URL'))
        ) || {};

      const callBackUrl = casinoGameUrl
        ? `${casinoGameUrl.pathname}?${casinoGameUrl.search}`
        : prevRoutes?.login
        ? prevRoutes?.login
        : '';

      if (queries.login && signInPopupAlias) {
        LocalStorage.setItem(
          storageKeyName('account', 'POPUP_OPEN'),
          signInPopupAlias
        );

        LocalStorage.removeItem(storageKeyName('casino', 'SELECTED_GAME_URL'));

        if (window.fromNext) {
          UtilsExternal.openNativePopup(signInPopupAlias);
        } else {
          window.openPopup?.(signInPopupAlias, true, callBackUrl);
        }
      }

      if (queries.register && signUpPopupAlias) {
        LocalStorage.setItem(
          storageKeyName('account', 'POPUP_OPEN'),
          signUpPopupAlias
        );

        if (window.fromNext) {
          UtilsExternal.openNativePopup(signUpPopupAlias);
        } else {
          window.openPopup?.(signUpPopupAlias, true, callBackUrl);
        }
      }
    }
  }, [openCustomPopup, isLoggedIn, user.pending]);

  useEffect(() => {
    const authData = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))
    );

    if (!authData && !queries.accounts) {
      LocalStorage.removeItem(storageKeyName('casino', 'SELECTED_GAME_URL'));
    }
  }, [isLoggedIn, queries.accounts]);

  useEffect(() => {
    if (typeof window.selfExclude !== 'function') {
      window.selfExclude = (
        periodObj = {
          value: 1,
          label: '1 days',
          type: 'days',
          amount: 1,
          show: true,
          name: 'days-1',
          user,
          reason: '11'
        },
        exclusionType = 1
      ) => {
        setSelfExclusion(periodObj, exclusionType, accountLogout);
      };
    }

    if (signInPopupAlias || signUpPopupAlias) {
      window.addEventListener('message', handleCustomPopupClose, false);
    }

    return () => {
      window.removeEventListener('message', handleCustomPopupClose);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const searchObject = QueryString.parse(window.location.search, {
        ignoreQueryPrefix: true
      });

      if (searchObject && searchObject?.kycaid) {
        history.replace('/', {});
        showToastSuccess(t('account.success'));
      }
    }

    if (!isLoggedIn) {
      dispatch(resetPayments());
      dispatch(setPaymentDepositPromo(null));
    }
  }, [isLoggedIn]);

  useLayoutEffect(() => {
    if (window.refreshWhenLoggedIn || (customPopupOpen && isInPopup)) {
      const afterSignIn = new AfterSignIn();
      afterSignIn.setFinalExecutionCallback(() => {
        history.push(
          addQueryInString(getSavedLocation(undefined, true, true, true), {
            login: undefined,
            register: undefined
          })
        );

        if (window.fromNext) {
          LocalStorage.removeItem(storageKeyName('account', 'POPUP_OPEN'));
          UtilsExternal.closeNativePopup();

          return;
        }

        if (window.refreshWhenLoggedIn) {
          GLOBALS.casino.blockCasinoGamePopupOpening = true;
        }

        if (customPopupOpen && isInPopup) {
          window.parent.closePopup(customPopupAlias);

          LocalStorage.removeItem(storageKeyName('account', 'POPUP_OPEN'));

          if (window.refreshWhenLoggedIn) {
            setTimeout(() => {
              window.parent.location.reload();
            }, 0);
          }
        } else {
          if (window.refreshWhenLoggedIn) {
            window.location.reload();
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    if (
      isLoggedIn &&
      checkUserStatus.bankIdIntergration(user) &&
      (SpringConfigs.IS_BANK_ID_INTEGRATION_ENABLED ||
        +accountParams?.diiaVerification === 1)
    ) {
      setShowBankModal(true);
    } else {
      setShowBankModal(false);
    }
  }, [isLoggedIn, user.active_step, user.active_step_state]);

  useEffect(() => {
    if (isMobile()) {
      if (queries.accounts) {
        document.documentElement.classList.add(
          'accountModal--html--overflow--hidden'
        );
        document.body.classList.add('accountModal--html--overflow--hidden');
      } else {
        document.documentElement.classList.remove(
          'accountModal--html--overflow--hidden'
        );
        document.body.classList.remove('accountModal--html--overflow--hidden');
      }
    }
  }, [queries.accounts]);

  const showCounterOfferModals = useMemo(() => {
    return !(
      pendingSuperBets === null || Object.keys(pendingSuperBets).length === 0
    );
  }, [pendingSuperBets]);

  const onClose = useCallback((val: boolean) => {
    setVisible(val);
    LocalStorage.removeItem(
      storageKeyName('account', 'OPEN_CHECK_EMAIL_MODAL')
    );
  }, []);

  const sessionVisibilityRefresh = JSON.parse(
    LocalStorage.getItem(
      storageKeyName('account', 'SESSION_VISIBILITY_REFRESH')
    )
  );

  if (
    window.refreshWhenLoggedIn &&
    SpringConfigs.ADD_INFO_AFTER_LOGIN &&
    showAdditionalInfo
  ) {
    LocalStorage.setItem(
      storageKeyName('account', 'SESSION_VISIBILITY_REFRESH'),
      JSON.stringify(1)
    );
  }

  const removeUser = (): void => {
    dispatch(removeUserData());
    dispatch(setClientGamingStateInfo(null));

    LocalStorage.removeItem(
      storageKeyName('account', 'LOGIN_LIMIT_START_TIME')
    );

    if (window.refreshWhenLoggedIn && getCookie('sbuser')) {
      removeUserFromStorage();
      window.parent.location.reload();
    }
  };

  useEffect(() => {
    const authData = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))
    );

    console.info(reConnected, 'reConnected', authData);
    console.info(isConnected, 'isConnected');

    if (!authData?.auth_token) {
      removeUser();
    }
  }, [reConnected, isConnected]);

  useEffect(() => {
    if (
      SpringConfigs.IFRAME_SPORTSBOOK &&
      queries.user_id &&
      queries.auth_token
    ) {
      restoreLogin({
        auth_token: queries.auth_token,
        user_id: queries.user_id
      });
    }
  }, [queries.user_id, queries.auth_token]);

  const showDocumentUploadModal =
    (user.active_step === userActiveStep.USER_ACTIVE_STEP_VALUE_27 &&
      user.active_step_state ===
        userActiveStep.USER_ACTIVE_STEP_STATE_VALUE_5) ||
    (SpringConfigs.SELFIE_WITH_DOCUMENTS && !user.is_verified);

  const showAutoidentVerificationModal = useMemo(() => {
    const checkAutoident =
      !!(
        SpringConfigs.MGA_ACCOUNT_TYPE || SpringConfigs.CANADIAN_ACCOUNT_TYPE
      ) &&
      user.active_step === userActiveStep.USER_ACTIVE_STEP_VALUE_33 &&
      user.active_step_state === userActiveStep.USER_ACTIVE_STEP_STATE_VALUE_5;

    dispatch(setShowAutoidentVerificationModal(checkAutoident));

    return checkAutoident;
  }, [user]);

  const showVerifyAccountModal =
    SpringConfigs.SHOW_VERIFY_ACCOUNT_MODAL &&
    user.active_step === userActiveStep.USER_ACTIVE_STEP_VALUE_50 &&
    user.active_step_state === userActiveStep.USER_ACTIVE_STEP_STATE_VALUE_5;

  const showNotVerifiedMessage =
    (SpringConfigs.SHOW_BRAZIL_USER_UNVERIFIED_MESSAGE &&
      user.active_step_state ===
        userActiveStep.USER_ACTIVE_STEP_STATE_VALUE_5 &&
      user.active_step === userActiveStep.USER_ACTIVE_STEP_VALUE_41) ||
    (SpringConfigs.SHOW_USER_UNVERIFIED_MESSAGE &&
      user.active_step_state ===
        userActiveStep.USER_ACTIVE_STEP_STATE_VALUE_5 &&
      user.active_step === userActiveStep.USER_ACTIVE_STEP_VALUE_40) ||
    (Boolean(Number(accountParams?.accountVerificationReminder)) &&
      !user.is_verified);

  useEffect(() => {
    if (isLoggedIn && !userOptIns && shouldGetOptIns) {
      getPlayerOptIns((resp: UserOptInsResponse) => {
        if (resp && resp.details.length) {
          dispatch(setUserOptIns(resp.details));
        }
      });
    }
  }, [isLoggedIn, userOptIns, shouldGetOptIns]);

  useEffect(() => {
    if (queries.accounts) {
      queries.login &&
        !routeHistory.includes('login') &&
        setRouteHistory(prev => [...prev, 'login']);
      queries.register &&
        !routeHistory.includes('register') &&
        setRouteHistory(prev => [...prev, 'register']);
    }
  }, [queries.accounts]);

  useEffect(() => {
    if (isMobile() && queries.login) {
      import(
        /* webpackChunkName: "accounts-nested-modal-login" */ 'components/Login/Nested'
      );
      import(/* webpackChunkName: "login" */ './views/Login');
    }

    if (window.fromNext) {
      import(/* webpackChunkName: "accounts-login" */ 'components/Login');

      if (isMobile()) {
        import(
          /* webpackChunkName: "accounts-mobile-layout" */ './layout/Mobile'
        );
      } else {
        import(
          /* webpackChunkName: "accounts-desktop-layout" */ './layout/Desktop'
        );
      }
    }
  }, [routeHistory, queries.accounts]);

  useEffect(() => {
    if (!SpringConfigs.ACCOUNT_REGISTRATION_JSON_PATH && !regFieldsData) {
      dispatch(setRegField(RegFieldsDefaultsJson));

      return;
    }

    !regFieldsData &&
      getAccountJson(SpringConfigs.ACCOUNT_REGISTRATION_JSON_PATH)
        .then(data => {
          dispatch(setRegField(data));
        })
        .catch(() => {
          dispatch(setRegField(RegFieldsDefaultsJson));
        });
  }, [queries.register, onPageCurrentSlide]);

  const termsAndConditionsShowModal = useMemo(() => {
    const userVersion = Number(user?.terms_and_conditions_version);
    const currentVersion = Number(accountParams?.tcVersion);

    return (
      +accountParams?.termsConditionsCustom === 1 &&
      userVersion < currentVersion &&
      SpringConfigs.TERMS_POPUP_LINK?.length
    );
  }, [
    user?.terms_and_conditions_version,
    accountParams?.tcVersion,
    accountParams?.termsConditionsCustom
  ]);

  useEffect(() => {
    isRegFinished && handleSetVisible();
  }, [isRegFinished]);

  const handleSetVisible = () => {
    const checkEmailModal = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'OPEN_CHECK_EMAIL_MODAL'))
    );

    if (checkEmailModal) {
      setVisible(true);
    }
  };

  const handleCustomPopupClose = (event: MessageEvent) => {
    if (event.data.action !== 'closePopup') {
      return;
    }

    const authData = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))
    );

    if (event.data.url && authData) {
      LocalStorage.removeItem(storageKeyName('account', 'POPUP_OPEN'));
      history.push(event.data.url);

      return;
    }

    if (!accountParams?.autologinX) {
      history.push(
        updateQuery({
          accounts: undefined,
          login: undefined,
          register: undefined
        })
      );
      handleSetVisible();
    }

    const location = window.location.search;

    if (
      location.includes('accounts=') &&
      (location.includes('login=') || location.includes('register='))
    ) {
      LocalStorage.removeItem(storageKeyName('account', 'POPUP_OPEN'));
      history.push(
        updateQuery({
          accounts: undefined,
          login: undefined,
          register: undefined
        })
      );
    }
  };

  const shouldShowPlayerInfo = () => {
    if (!accountSettings) {
      return false;
    }

    if (!accountSettings?.playerInfoShowDate) {
      return true;
    }

    const today = dayjs();
    const playerInfoShowDate = dayjs(
      accountSettings.playerInfoShowDate,
      'YYYY/MM/DD HH:mm:ss'
    );

    const diffInHours = today.diff(playerInfoShowDate, 'hour', true);

    if (!dayjs(playerInfoShowDate).isValid() || diffInHours < 0) {
      return true;
    }

    return diffInHours >= HOUR_PER_DAY;
  };

  if (
    SpringConfigs.SHOW_ACCOUNT_ADDITIONAL_INFO_MODAL &&
    isLoggedIn &&
    additionalInfoPopupInLS == null
  ) {
    LocalStorage.setItem(
      storageKeyName('account', 'ADDITIONAL_INFO_POPUP'),
      JSON.stringify({
        count: 1
      })
    );
  }

  if (fromIframe) {
    return null;
  }

  return (
    <>
      {favoritePopupOpen && !!Number(favorite) && (
        <Suspense fallback={null}>
          <FavoriteOnHeaderPopup favoriteTypes={favoriteTabs} />
        </Suspense>
      )}
      {SpringConfigs.GPS_TRACKING_ENABLED && (
        <Suspense fallback={null}>
          <GpsTracking />
        </Suspense>
      )}
      {isLoggedIn && !user.pending && accountSections?.myProfile && (
        <>
          {SpringConfigs.IOVATION_ENABLED &&
            !SpringConfigs.IOVATION_HIDE_UPLOAD &&
            showDocumentUploadModal && (
              <Suspense>
                <DocumentUploadModal />
              </Suspense>
            )}
          {showVerifyAccountModal && (
            <Suspense>
              <VerifyAccountModalRedirect />
            </Suspense>
          )}
          {SpringConfigs.DOC_UPLOAD_TRU_NARRATIVE &&
            showDocumentUploadModal && (
              <Suspense>
                <DocumentUploadModal />
              </Suspense>
            )}
          {SpringConfigs.LOGIN_FREEZE && (
            <Suspense>
              <LoginFreeze />
            </Suspense>
          )}
          {SpringConfigs.PLAYER_ACTIVITY_CHECK && (
            <Suspense>
              <PlayerActivityChecker />
            </Suspense>
          )}
          {((SpringConfigs.ADD_INFO_AFTER_LOGIN &&
            (showAdditionalInfo || showAdditionalInfoLS)) ||
            sessionVisibilityRefresh) &&
            shouldShowPlayerInfo() && (
              <Suspense>
                <PlayerInfo />
              </Suspense>
            )}

          {SpringConfigs.SHOW_ACCOUNT_ADDITIONAL_INFO_MODAL &&
            additionalInfoPopupInLS?.count === 1 && (
              <Suspense>
                <AdditionalInfoPopup />
              </Suspense>
            )}

          {showAutoidentVerificationModal && (
            <Suspense>
              <AutoidentVerificationModal />
            </Suspense>
          )}
          {showCounterOfferModals && (
            <Suspense>
              <CounterOfferModal />
            </Suspense>
          )}
          {termsAndConditionsShowModal && (
            <Suspense>
              <TermsAndConditions />
            </Suspense>
          )}
          {showNotVerifiedMessage ? (
            <Suspense>
              <NotVerifiedMessage />
            </Suspense>
          ) : null}
        </>
      )}
      {auth_data_x && !isInPopup ? (
        <div
          className={cc([
            'profileInfo__container',
            { 'profileInfo__container--mobile': isMobile() }
          ])}
        >
          {!!partnerConfigs?.secondary_wallet_currencies?.length && (
            <Suspense>
              <CryptoWallets />
            </Suspense>
          )}
          <Suspense>
            <ProfileInfo
              data={auth_data_x}
              favoriteEnabled={!!Number(favorite)}
              favoriteTabs={favoriteTabs}
            />
          </Suspense>
        </div>
      ) : onPageCurrentSlide === 'signin' ? (
        <div className="x-account__onPageWrapper">
          <Suspense fallback={<Loading />}>
            <Login
              onPage
              inline
              horizontal={horizontalView}
              setOnPageCurrentSlide={setOnPageCurrentSlide}
              accountSignUpButtonStyle={accountSignUpButtonStyle}
              accountSignInButtonStyle={accountSignInButtonStyle}
              accountButtonsDistance={accountButtonsDistance}
              signInBtn={signInBtn}
              signUpBtn={signUpBtn}
              signUpPopupAlias={signUpPopupAlias}
              inPopup={isInPopup}
            />
          </Suspense>
          {SpringConfigs.THEME_SWITCHER && !isMobile() && !isInPopup && (
            <Suspense fallback={<ChangeThemeSkeleton />}>
              <ChangeTheme />
            </Suspense>
          )}
        </div>
      ) : onPageCurrentSlide === 'signup' && !openCustomPopup ? (
        <div className="x-account__onPageWrapper">
          <Suspense fallback={<Loading />}>
            <RegisterOnPage
              accountSignUpButtonStyle={accountSignUpButtonStyle}
              setOnPageCurrentSlide={setOnPageCurrentSlide}
              onPageCurrentSlide={onPageCurrentSlide}
              accountPrevButtonStyle={accountPrevButtonStyle}
              accountNextButtonStyle={accountNextButtonStyle}
              signInBtn={signInBtn}
              signUpBtn={signUpBtn}
              inPopup={inPopup}
            />
          </Suspense>
        </div>
      ) : (
        <Suspense fallback={<AuthSectionSkeleton {...configs} />}>
          <AuthSection {...configs} />
        </Suspense>
      )}
      {mounted && !openCustomPopup && !customPopupOpen && (
        <Popup
          open={isMobile() && !!queries.accounts}
          animationType="toLeft"
          className={cc([
            'account-popup',
            {
              'register-popup': queries.register,
              'account-popup-open': isMobile() && !!queries.accounts
            }
          ])}
        >
          {isMobile() &&
            !openCustomPopup &&
            Object.keys(queries).length > 1 &&
            queries.accounts &&
            !customPopupOpen && (
              <Pages
                setVisible={onClose}
                accountSignUpButtonStyle={accountSignUpButtonStyle}
                accountPrevButtonStyle={accountPrevButtonStyle}
                accountNextButtonStyle={accountNextButtonStyle}
                signInBtn={signInBtn}
                signUpBtn={signUpBtn}
              />
            )}
        </Popup>
      )}

      {!openCustomPopup &&
        !isMobile() &&
        Object.keys(queries).length > 1 &&
        queries.accounts &&
        !customPopupOpen && (
          <Pages
            setVisible={setVisible}
            accountSignUpButtonStyle={accountSignUpButtonStyle}
            accountPrevButtonStyle={accountPrevButtonStyle}
            accountNextButtonStyle={accountNextButtonStyle}
            signInBtn={signInBtn}
            signUpBtn={signUpBtn}
          />
        )}
      <Suspense>
        <QueryRoute
          includes={['verify', 'code']}
          excludes={[]}
          component={VerifyEmailModal}
        />
      </Suspense>

      {/* portal here */}

      {showBankModal && (
        <Suspense>
          <BankIDIntegration
            visible={showBankModal}
            close={() => setShowBankModal(false)}
          />
        </Suspense>
      )}

      {visible && (
        <Suspense>
          <CheckEmailModal visible={visible} setVisible={onClose} />
        </Suspense>
      )}

      {!!remainingSessionDuration?.isOpen && (
        <Suspense>
          <RemainingSessionDurationModal />
        </Suspense>
      )}

      {queries.accounts && (
        <Suspense>
          <AccountGenericModals />
        </Suspense>
      )}

      {isLoggedIn &&
        (Boolean(loyaltyNotifications?.length) ||
          Boolean(cashbackNotifications?.length)) && (
          <Suspense>
            <NotificationCards />
          </Suspense>
        )}

      {isLoggedIn && isLoyaltyV2 && (
        <Portal>
          <Suspense>
            <LoyaltyCurrentLevelBadge />
          </Suspense>
        </Portal>
      )}
    </>
  );
};
